import React from 'react'

import VideoPlayerComponent from '../../../sharedLegacy/components/VideoPlayerComponent/VideoPlayerComponent'
import { VideoPreviewLayout } from '../video-preview/layout/VideoPreviewLayout'
import { VideoPreviewSettingsProvider } from '../video-preview/VideoPreviewSettingsContext'
import { VideoPreviewLogo } from '../video-preview/common/VideoPreviewLogo'
import { VideoPreviewTitle } from '../video-preview/common/VideoPreviewTitle'
import { VideoPreviewDescription } from '../video-preview/common/VideoPreviewDescription'
import { getCardBackgroundFromSettings } from '../video-preview/common/VideoPreviewCardBackground'
import { WidgetBusinessCard } from '../video-preview/common/widgets/business-card/WidgetBusinessCard'
import { WidgetButton } from '../video-preview/common/widgets/button/WidgetButton'
import { WidgetCalendly } from '../video-preview/common/widgets/calendly/WidgetCalendly'
import { WidgetEmbed } from '../video-preview/common/widgets/embed/WidgetEmbed'
import { WidgetHubspot } from '../video-preview/common/widgets/hubspot/WidgetHubspot'
import type { WidgetDTO } from '../../model/Widget'
import {
  buildPublicSettings,
  usePublicSettingsQuery,
  usePublicVideoQuery,
  usePublicWidgetsQuery,
} from '../../blocs/usePublicVideoPreview'

import { PublicVideoPreviewPageSkeleton } from './PublicVideoPreviewPageSkeleton'

import '../video-preview/common/VideoPreviewWidgetsSidebar.scss'
import '../video-preview/VideoPreview.scss'

const WIDGET_COMPONENTS: Record<
  string,
  React.ComponentType<{
    widget: WidgetDTO
    isEditable?: boolean
    onSave?: (values: Partial<WidgetDTO>) => Promise<void>
    onDelete?: () => void
    onCancel?: () => void
  }> | null
> = {
  button: WidgetButton,
  embed: WidgetEmbed,
  businesscard: WidgetBusinessCard,
  calendly: WidgetCalendly,
  hubspotcalendar: WidgetHubspot,
}

const noop = async () => {}
const noopSync = () => {}

interface PublicVideoPreviewPageProps {
  videoId: number
  videoToken: string
  siteUrl: string
}

export const PublicVideoPreviewPage = ({
  videoId,
  videoToken,
  siteUrl,
}: PublicVideoPreviewPageProps) => {
  const { data: video, isLoading: isLoadingVideo } = usePublicVideoQuery(
    videoId,
    videoToken,
    siteUrl
  )
  const { data: widgets = [], isLoading: isLoadingWidgets } =
    usePublicWidgetsQuery(videoId, videoToken, siteUrl, !!video)
  const { data: settingsData, isLoading: isLoadingSettings } =
    usePublicSettingsQuery(siteUrl)

  if (isLoadingVideo || isLoadingWidgets || isLoadingSettings) {
    return <PublicVideoPreviewPageSkeleton />
  }

  if (!video) return null

  const settings = buildPublicSettings(settingsData, siteUrl)
  const cardBackground = getCardBackgroundFromSettings(settings)

  const visibleWidgets = widgets.filter(
    (w) => !w.empty_p && WIDGET_COMPONENTS[w.type]
  )

  const sidebar =
    visibleWidgets.length > 0 ? (
      <div className="video-preview-widgets-sidebar">
        <div className="video-preview-widgets-list">
          {visibleWidgets.map((widget) => {
            const WidgetComponent = WIDGET_COMPONENTS[widget.type]
            if (!WidgetComponent) return null
            return (
              <div
                key={widget.video_widget_id}
                className="video-preview-widget-item"
              >
                <WidgetComponent
                  widget={widget}
                  isEditable={false}
                  onSave={noop}
                  onDelete={noopSync}
                  onCancel={noopSync}
                />
              </div>
            )
          })}
        </div>
      </div>
    ) : undefined

  return (
    <VideoPreviewSettingsProvider settings={settings}>
      <VideoPreviewLayout
        isPreview
        className="video-page-view"
        cardBackground={cardBackground}
        logo={<VideoPreviewLogo />}
        videoPlayer={
          <div className="video-preview-player-shell">
            <VideoPlayerComponent
              object={video}
              objectType="photo"
              options={{
                source: 'site',
                scrubberColor: settings.scrubberColor,
                showLogo: 0,
              }}
            />
          </div>
        }
        title={
          <VideoPreviewTitle
            videoId={video.photo_id}
            title={video.title}
            isEditable={false}
          />
        }
        description={
          <VideoPreviewDescription
            videoId={video.photo_id}
            content={video.content}
            isEditable={false}
          />
        }
        sidebar={sidebar}
      />
    </VideoPreviewSettingsProvider>
  )
}
